import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AccountEventsLayout } from '@/layouts/AccountEventsLayout';
import { conclaveApi } from '@/lib/conclave-api';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateStandaloneConclavePage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ticketName, setTicketName] = useState('General admission');
  const [ticketPrice, setTicketPrice] = useState(0);
  const [saving, setSaving] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const start = startDate ? new Date(startDate).toISOString() : '';
      const end = endDate ? new Date(endDate).toISOString() : start;
      const created = await conclaveApi.createStandaloneEvent({
        communityId: null,
        title,
        description,
        address: address || undefined,
        locationType: address ? 'PHYSICAL' : 'TO_BE_DECIDED',
        privacy: 'PUBLIC',
        eventType: 'CONCLAVE',
        startDate: start,
        endDate: end,
        isQuickRegistration: true,
        tickets: [
          {
            name: ticketName || 'General admission',
            type: 'OPEN_FOR_ALL',
            price: Number(ticketPrice) || 0,
            quantity: null,
            allowedAttendeeTypes: ['SELF'],
            formSchema: [],
          },
        ],
      });
      toast.success('Conclave created');
      navigate(`/e/${created.id}`, { replace: true });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data
          ?.error ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to create event';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AccountEventsLayout>
      <Button variant="ghost" className="mb-4 pl-0 hover:bg-transparent hover:text-primary" asChild>
        <Link to="/events">
          <ArrowLeft className="mr-2 h-4 w-4" /> All events
        </Link>
      </Button>
      <h1 className="text-2xl font-semibold tracking-tight">Create conclave</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Public networking event — attendees register with their Milora account. No community required.
      </p>

      <Card className="glass-card mt-6">
        <CardContent className="pt-6">
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Venue / address</Label>
              <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="start">Start</Label>
              <Input
                id="start"
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end">End</Label>
              <Input
                id="end"
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticketName">Ticket name</Label>
              <Input
                id="ticketName"
                value={ticketName}
                onChange={(e) => setTicketName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticketPrice">Ticket price (INR)</Label>
              <Input
                id="ticketPrice"
                type="number"
                min={0}
                value={ticketPrice}
                onChange={(e) => setTicketPrice(Number(e.target.value))}
              />
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={saving}>
              {saving ? 'Creating…' : 'Create conclave'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AccountEventsLayout>
  );
}
